import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArchiveProducerDialog } from './ArchiveProducerDialog';
import { DeleteProducerDialog } from './DeleteProducerDialog';

interface DangerZoneSectionProps {
  producerId: string;
  producerName: string;
  producerStatus: string;
  canManage: boolean;
  onArchived?: () => void;
  onDeleted?: () => void;
}

export function DangerZoneSection({
  producerId,
  producerName,
  producerStatus,
  canManage,
  onArchived,
  onDeleted,
}: DangerZoneSectionProps) {
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  if (!canManage) {
    return null;
  }

  const isArchived = producerStatus === 'archived';

  return (
    <>
      <Card className="border-destructive/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Farlig zon</CardTitle>
          </div>
          <CardDescription>
            Åtgärder här påverkar gården permanent eller döljer den från publika ytor.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Archive section */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg border bg-muted/30">
            <div className="space-y-1">
              <p className="font-medium">
                {isArchived ? 'Återaktivera gård' : 'Arkivera gård'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isArchived
                  ? 'Gör gården synlig igen för kunder.'
                  : 'Gården blir osynlig för kunder och kan återaktiveras senare.'}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setArchiveDialogOpen(true)}
            >
              {isArchived ? 'Återaktivera' : 'Arkivera gård'}
            </Button>
          </div>

          {/* Delete section */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg border border-destructive/30 bg-destructive/5">
            <div className="space-y-1">
              <p className="font-medium text-destructive">Radera permanent</p>
              <p className="text-sm text-muted-foreground">
                Kan inte ångras. Tar bort gården och all associerad data (produkter, event, bokningar, ordrar).
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Radera permanent
            </Button>
          </div>
        </CardContent>
      </Card>

      <ArchiveProducerDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        producerId={producerId}
        producerName={producerName}
        isArchived={isArchived}
        onSuccess={onArchived}
      />

      <DeleteProducerDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        producerId={producerId}
        producerName={producerName}
        onSuccess={onDeleted}
      />
    </>
  );
}
